import React, { useEffect, useState } from 'react'
import { Meta, Title } from 'react-head'
import Header from 'js/pages/LandingPages/components/Header'
import Hero from 'js/pages/LandingPages/components/Hero'
import HowItWorks from 'js/pages/LandingPages/components/HowItWorks'
import Testimonials from 'js/pages/LandingPages/components/Testimonials'
import PromotionBlock from 'js/pages/LandingPages/components/PromotionBlock'
import MealPlans from 'js/pages/LandingPages/components/MealPlans'
import FeaturesBlock from 'js/pages/LandingPages/components/FeaturesBlock'
import ReviewsBlock from 'js/pages/LandingPages/components/ReviewsBlock'
import HighlightedFeatures from 'js/pages/LandingPages/components/HighlightedFeatures'
import ScienceSection from 'js/pages/LandingPages/components/ScienceSection'
import Faq from 'js/pages/LandingPages/components/Faq'
import Modal from 'js/pages/LandingPages/components/Modal'
import { runQuery } from 'js/dato'
import withSSR from 'js/components/withSSR/withSSR'
import { PerformanceLandingPage_performanceLandingPage } from './types/PerformanceLandingPage'
import ErrorPage from '../ErrorPage'
import QrCode from 'js/pages/LandingPages/components/QrCode'
import datoLocales from 'js/datoLocales'
import useLocale from 'js/hooks/useLocale'
import { getSlugLanguage } from 'js/locale/localizationHelper'
import { setEntryPoint } from 'js/analytics'
import query from './query'

const code = '50web23'

const blocks = {
  HeroBlockRecord: Hero,
  FaqBlockRecord: Faq,
  HowItWorksBlockRecord: HowItWorks,
  LandingPageTestimonialsBlockRecord: Testimonials,
  PromotionBlockRecord: PromotionBlock,
  MealPlansBlockRecord: MealPlans,
  FeaturesBlockRecord: FeaturesBlock,
  ReviewsBlockRecord: ReviewsBlock,
  HighlightedFeaturesBlockRecord: HighlightedFeatures,
  ScienceBlockRecord: ScienceSection,
} as const

type Props = {
  data: PerformanceLandingPage_performanceLandingPage
  isLoading: boolean
  error: Error
}

type MissingTranslationMessageProps = {
  name: string
  id: string
  language: string
}
const MissingTranslationMessage = ({
  name,
  id,
  language,
}: MissingTranslationMessageProps) => (
  <div className="my-12 bg-red p-12 text-black">
    <div>Missing translation</div>
    <p>
      The &quot;performance landing page section&quot; record named:{' '}
      <strong className="font-bold">{name}</strong> (with id: {id}) is missing a
      translation to <strong className="font-bold">{language}</strong>
    </p>
    <p>
      <a
        href={`https://lifesum.admin.datocms.com/editor/item_types/TLheY4ICSlKEKcUPyiGVYA/items/${id}`}
      >
        Add a translation ({language}) in Dato CMS
      </a>
    </p>
  </div>
)

const General = ({ data, isLoading, error }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const { language } = useLocale()

  useEffect(() => {
    setEntryPoint('performance-landing-page', code)
  }, [])

  if (isLoading) {
    return <>Loading...</>
  }
  if (error) return <ErrorPage error={error} />

  const Spacer = () => (
    <div className="w-screen sm:py-[25px] md:py-[45px]"></div>
  )

  return (
    <>
      <Title>{data?.seo?.title}</Title>
      <Meta name="description" content={data?.seo?.description || ''} />
      <div className="bg-BgMain pb-56">
        <Header
          locales={data?._locales}
          inverted={false}
          // @ts-ignore
          signupLinkText={data?.signupLinkText as string}
          // @ts-ignore
          downloadLinkText={data?.downloadLinkText as string}
        />
        {data?.sections.map((section, index) => {
          const content = section.contentBlocks[0]
          const sectionName = content?.__typename
          const Component = blocks[sectionName]

          if (!content) {
            return (
              <MissingTranslationMessage
                id={section.id}
                language={language}
                key={index}
                name={section?.name as string}
              />
            )
          }
          if (!Component) {
            return (
              <div className="my-12 bg-red p-12 text-black" key={index}>
                Developer error: Missing component for{' '}
                <span className="font-bold">{sectionName}</span>
              </div>
            )
          }
          return (
            <div key={index}>
              <Component
                // @ts-ignore
                onOpenModal={setIsModalOpen}
                // @ts-ignore
                signupLinkText={data?.signupLinkText as string}
                // @ts-ignore
                downloadLinkText={data?.downloadLinkText as string}
                // @ts-ignore
                downloadLinkFirst={data?.downloadLinkFirst as boolean}
                {...content}
              />
              <Spacer />
            </div>
          )
        })}
        <QrCode />
        {isModalOpen && <Modal onClose={() => setIsModalOpen(false)} />}
      </div>
    </>
  )
}

General.getInitialProps = ({ isPreview, params, location }) => {
  const locale = getSlugLanguage(location.pathname) || 'en'
  const slug = params.slug ? params.slug : 'general'
  const datoLocale = datoLocales[locale]

  return runQuery(query, { slug, language: datoLocale }, isPreview).then(
    ({ performanceLandingPage }) => {
      if (!performanceLandingPage._locales.includes(locale)) {
        return
      }

      if (!performanceLandingPage) {
        return
      }

      return performanceLandingPage
    },
  )
}

export default withSSR(General)
